/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

//MTH component import
import NoMoreRegister from '../../component/NoMoreRegister';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox, {
  SLOUAddressSelectBox,
} from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  getCompositionsSLUnit,
  postCompositionsSLUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {slInfoSort} from '../../functions/sort';
import {format, limit, pullDownMenu} from '../../define';
import {useTranslation} from 'react-i18next';
import {
  PrimaryButton,
  SecondaryButton,
  AddButton,
} from '../../component/BasicButton';

/**
 * 親機として登録されている数(子機の数)を求める
 * @param parentList 親機として登録されているユニットIDの一覧
 * @param unitID ユニットID
 * @returns 親機として登録されている数
 */
const asParentUseCount = (
  parentList: (string | undefined)[],
  unitID: string
) => {
  return parentList.reduce((result, parentID) => {
    if (parentID === unitID) result++;
    return result;
  }, 0);
};

/**
 * 室外機追加ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const OUAddDialog = (props: {
  /** 室外機追加ボタンの名前 */
  addButtonName: string;
  /** 室内外機の情報 */
  slInfo: apiIF.res.getCompositionsSLUnit | null;
  /** 室内外機の情報（set関数） */
  setSLInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsSLUnit | null>
  >;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
  /** SL新旧設定 */
  slType: 'new' | 'old';
}) => {
  const trans = useTranslation().t; //翻訳関数

  //パスパラメータ取得
  const {siteID, gwID, systemType} = useParams();

  const [ouName, setOUName] = useState(''); //室外機の名前
  const [ouAddress, setOUAddress] = useState(''); //SLアドレス
  //prettier-ignore
  const [ouType, setOUType] = useState<apiIF.reqParameters.postCompositionsSLUnit['outdoor-unit-type']>('KXZ'); //室外機のタイプ
  const [ouParent, setParent] = useState('0'); //親子室外機設定
  //prettier-ignore
  const [ouPowerSupply, setOUPowerSupply] = useState<apiIF.reqParameters.postCompositionsSLUnit['power-supply']>('threePhase'); //電源の仕様
  const [ouVoltage, setOUVoltage] = useState('0'); //入力電圧
  const [isOpenDialog, setIsOpenDialog] = useState(false); //ダイアログ表示フラグ（追加ダイアログ・登録超過ダイアログ）
  const [okBtnDisabled, setOKBtnDisabled] = useState(true); //OKボタン非表示フラグ
  const [ouNumber, setOUNumber] = useState(0); //室外機台数
  const [parentSelectionItem, setParentSelectionItem] = useState([
    {
      value: '0',
      label: trans('OUAddDialog.superlink.dialogBox.txtField.value.none'),
    },
  ]); //親子室外機設定選択項目
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [ouNameError, setOUNameError] = useState(false); //室外機の名前
  const [ouVoltageError, setOUVoltageError] = useState(false); //入力電圧

  //入力項目のチェック
  useEffect(() => {
    if (
      ouName === '' ||
      ouAddress === '' ||
      ouNameError ||
      apiWait ||
      ouVoltageError
    ) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [ouName, ouAddress, ouVoltageError, ouNameError, apiWait]);

  //親子室外機設定選択項目作成
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      systemType === undefined
    ) {
      return;
    }

    //室内外機の情報を取得
    getCompositionsSLUnit(siteID, gwID, {'max-result': 400}).then(response => {
      //SLアドレス順にソート
      response['outdoor-unit-informations'].sort((a, b) => {
        return Number(a['sl-address']) - Number(b['sl-address']);
      });

      let tempParentSelectionItem = [
        {
          value: '0',
          label: trans('OUAddDialog.superlink.dialogBox.txtField.value.none'),
        },
      ];
      /** 親機として登録されているユニットIDの一覧 */
      const parentList = response['outdoor-unit-informations'].map(
        slInfo => slInfo['parent-outdoor-unit']
      );

      for (const ouInfo of response['outdoor-unit-informations']) {
        if (
          ouInfo['sl-system'] !== systemType.slice(2) ||
          ouInfo['outdoor-unit-name'] === undefined ||
          ouInfo['parent-outdoor-unit'] !== '' || //子機の場合
          asParentUseCount(parentList, ouInfo['outdoor-id']) >= 3 //子が3台以上登録されている場合
        ) {
          continue;
        }

        tempParentSelectionItem.push({
          value: ouInfo['outdoor-id'],
          label: ouInfo['outdoor-unit-name'],
        });
      }

      setParentSelectionItem(tempParentSelectionItem);
    });
  }, [props.slInfo, siteID, gwID, systemType]);

  //登録台数を算出
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (systemType === undefined || props.slInfo === null) {
      return;
    }

    let num = 0;

    for (const ouInfo of props.slInfo['outdoor-unit-informations']) {
      if (ouInfo['sl-system'] === systemType.slice(2)) {
        num++;
      }
    }
    setOUNumber(num);
  }, [props.slInfo, systemType]);

  /**
   * ADD outdoor unitボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenDialog(true);
  };

  /**
   * OKボタン押下時処理
   */
  const addOU = () => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      (systemType !== 'sl1' && systemType !== 'sl2')
    ) {
      return;
    }

    setAPIWait(true);

    //室外機を追加
    const addOUData: apiIF.reqParameters.postCompositionsSLUnit = {
      'outdoor-unit-name': ouName,
      'sl-system': systemType.slice(2) as '1' | '2',
      'sl-address': ouAddress,
      'outdoor-unit-type': ouType,
      'power-supply': ouPowerSupply,
      'input-voltage': ouVoltage,
      'parent-outdoor-unit': ouParent === '0' ? '' : ouParent,
    };
    postCompositionsSLUnit(siteID, gwID, addOUData)
      .then(() => {
        setAPIWait(false);
        if (ouParent !== '') {
          setParentSelectionItem([
            ...parentSelectionItem,
            {value: 'OUparent', label: ouName},
          ]);
        }
        //室内外機の情報を取得
        return getCompositionsSLUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        response = slInfoSort(response);
        props.setSLInfo(response);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
        handleClose();
      })
      .catch(() => {
        setAPIWait(false);
        setIsOpenErrorDialog(true);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setIsOpenDialog(false);
    setOUName('');
    setOUType('KXZ');
    setOUVoltage('0');
    setOUPowerSupply('threePhase');
    setOKBtnDisabled(true);
    setParent('0');
  };

  return (
    <>
      {/* 室外機追加ボタン */}
      <AddButton
        onClick={handleClickOpen}
        id={'mv-btnAddOu'}
        buttonText={props.addButtonName}
      />
      {/* 登録台数が上限に達している場合は、追加付加のメッセージを表示 */}
      {(props.slType === 'new' &&
        ouNumber >= limit.REGISTRATION_NUM_NEW_SL_OU_MAX) ||
      (props.slType === 'old' &&
        ouNumber >= limit.REGISTRATION_NUM_OLD_SL_OU_MAX) ? (
        <NoMoreRegister
          open={isOpenDialog}
          setOpen={setIsOpenDialog}
          title={trans('NoMoreRegist.errorDlg.title')}
          text={trans('NoMoreRegist.errorDlg.msg')}
        />
      ) : (
        <>
          {/* ダイアログ */}
          <Dialog
            open={isOpenDialog}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            id="addOuDialog">
            <DialogTitle id="form-dialog-title">
              {trans('OUAddDialog.superlink.dialogBox.title')}
            </DialogTitle>
            <DialogContent>
              {/* ダイアログに表示するメッセージ */}
              <DialogContentText>
                {trans('OUAddDialog.superlink.dialogBox.msg')}
              </DialogContentText>
              <Stack spacing={1.5}>
                {/* 室外機名称 */}
                <ChkInputTextbox
                  setValue={setOUName}
                  setError={setOUNameError}
                  chkLength={format.BASIC_LENGTH_MAX}
                  type="text"
                  label={trans(
                    'OUAddDialog.superlink.dialogBox.txtField.label.outdoorName'
                  )}
                  value={ouName}
                  id="mv-inputOuName"
                  class="gwSetting__contents__textBox--unitName"
                  helperText=" "
                  errHelperText={trans(
                    'OUAddDialog.superlink.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                  autoFocus={true}
                  margin="dense"
                  chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
                />
                {/* 室外機アドレス */}
                <SLOUAddressSelectBox
                  label={trans(
                    'OUAddDialog.superlink.dialogBox.txtField.label.superlinkAddress'
                  )}
                  setAddress={setOUAddress}
                  id="mv-selectOuUnit"
                  class="gwSetting__contents__textBox--address"
                />
                {/* 電圧 */}
                <ChkInputTextbox
                  setValue={setOUVoltage}
                  setError={setOUVoltageError}
                  chkInputTxtOK={format.VOLTAGE_PATTERN_OK}
                  type="text"
                  label={trans(
                    'OUAddDialog.superlink.dialogBox.txtField.label.voltage'
                  )}
                  value={ouVoltage}
                  id="mv-inputOuVoltage"
                  class="gwSetting__contents__textBox--voltage"
                  helperText=" "
                  errHelperText={trans(
                    'OUAddDialog.superlink.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                />
                {/* 電源の種類 */}
                <TextFieldSelectBox
                  label={trans(
                    'OUAddDialog.superlink.dialogBox.txtField.label.powerSupply'
                  )}
                  choices={pullDownMenu.POWER_SUPPLY_TYPE}
                  setValue={setOUPowerSupply}
                  default={ouPowerSupply}
                  id="mv-ouAddPowSplySelect"
                  class="gwSetting__contents__selectBox--powerSupply"
                />
                {/* 親室外機の選択 */}
                <TextFieldSelectBox
                  label={trans(
                    'OUAddDialog.superlink.dialogBox.txtField.label.parentOutdoor'
                  )}
                  choices={parentSelectionItem}
                  setValue={setParent}
                  default={ouParent}
                  id="mv-ouAddParentOuSelect"
                  class="gwSetting__contents__textBox--unitName"
                />
                {/* 室外機タイプを選択するラジオボタン(1st stepでは非表示とする) */}
                {/* <FormControl>
                  <FormLabel>Outdoor unit type</FormLabel>
                  <RadioGroup
                    row
                    defaultValue={ouType}
                    aria-label="position"
                    name="position">
                    <FormControlLabel
                      value="BeforeKXE6"
                      control={<Radio color="primary" id="mv-BeforeKXE6btn" />}
                      label="KXE6 or before"
                      onChange={() => setOUType('BeforeKXE6')}
                    />
                    <FormControlLabel
                      value="KXZ"
                      control={<Radio color="primary" id="mv-KXZbtn" />}
                      label="KXZ"
                      onChange={() => setOUType('KXZ')}
                    />
                    <FormControlLabel
                      value="KXZ2"
                      control={<Radio color="primary" id="mv-KXZ2btn" />}
                      label="KXZ2"
                      onChange={() => setOUType('KXZ2')}
                    />
                  </RadioGroup>
                </FormControl> */}
              </Stack>
            </DialogContent>
            <DialogActions>
              <SecondaryButton
                disabled={apiWait}
                onClick={handleClose}
                buttonText={trans(
                  'OUAddDialog.superlink.dialogBox.buttonTxt.cancel'
                )}
                id={'mv-ouAddCancelBtn'}
              />
              <PrimaryButton
                onClick={addOU}
                buttonText={trans(
                  'OUAddDialog.superlink.dialogBox.buttonTxt.ok'
                )}
                id="mv-btnOkAddOu"
                disabled={okBtnDisabled}
                className="BtnOk"
                apiWait={apiWait}
              />
            </DialogActions>
          </Dialog>
          <APIErrorMessage
            open={isOpenErrorDialog}
            setOpen={setIsOpenErrorDialog}
            id={'mv-ouAddErrDlgBtn'}
          />
        </>
      )}
    </>
  );
};
export default OUAddDialog;
